import { useEffect, useRef } from 'react';
import { Renderer, Program, Mesh, Triangle } from 'ogl';

const MAX_COLORS = 8;

const hexToRGB = hex => {
    const c = hex.replace('#', '').padEnd(6, '0');
    return [
        parseInt(c.slice(0, 2), 16) / 255,
        parseInt(c.slice(2, 4), 16) / 255,
        parseInt(c.slice(4, 6), 16) / 255
    ];
};

const prepStops = stops => {
    const base = (stops?.length ? stops : ['#FF9FFC', '#5227FF']).slice(0, MAX_COLORS);
    if (base.length === 1) base.push(base[0]);
    while (base.length < MAX_COLORS) base.push(base[base.length - 1]);

    return {
        arr: base.map(hexToRGB),
        count: Math.max(2, Math.min(MAX_COLORS, stops?.length ?? 2))
    };
};

const GradientBlinds = ({
    className,
    dpr,
    paused = false,
    gradientColors,
    angle = 0,
    noise = 0.3,
    blindCount = 16,
    blindMinWidth = 60,
    mouseDampening = 0.15,
    mirrorGradient = false,
    spotlightRadius = 0.5,
    spotlightSoftness = 1,
    spotlightOpacity = 1,
    distortAmount = 0,
    shineDirection = 'left',
    mixBlendMode = 'lighten',

    /* ▶️ Auto Animation */
    autoAnimate = true,
    autoSpeed = 1,
    pauseOnHover = true
}) => {
    const containerRef = useRef(null);
    const rendererRef = useRef(null);
    const programRef = useRef(null);
    const meshRef = useRef(null);
    const geometryRef = useRef(null);
    const rafRef = useRef(null);

    const mouseTargetRef = useRef([0, 0]);
    const autoCenterRef = useRef([0, 0]);
    const autoVelocityRef = useRef([0.6, 0.4]);
    const isPointerActiveRef = useRef(false);
    const firstResizeRef = useRef(true);
    const lastTimeRef = useRef(0);

    const resolveDynamicWidth = (value, containerWidth) => {
        if (typeof value === 'number') return value;

        if (typeof value === 'string') {
            const v = value.trim();

            if (v.endsWith('%')) {
                return containerWidth * (parseFloat(v) / 100);
            }

            if (v.endsWith('vw')) {
                return window.innerWidth * (parseFloat(v) / 100);
            }

            if (v.endsWith('cqw')) {
                // container query width ≈ container width
                return containerWidth * (parseFloat(v) / 100);
            }
        }

        return 0;
    };


    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const interactionLayer = document.querySelector('[data-bg-interaction]');

        /* ---------- Renderer ---------- */
        const renderer = new Renderer({
            dpr: dpr ?? window.devicePixelRatio ?? 1,
            alpha: true,
            antialias: true
        });
        rendererRef.current = renderer;

        const gl = renderer.gl;
        const canvas = gl.canvas;
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.display = 'block';
        container.appendChild(canvas);

        /* ---------- Shaders ---------- */
        const vertex = `
attribute vec2 position;
attribute vec2 uv;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

        const fragment = `
#ifdef GL_ES
precision mediump float;
#endif

uniform vec3  iResolution;
uniform vec2  iMouse;
uniform float iTime;

uniform float uAngle;
uniform float uNoise;
uniform float uBlindCount;
uniform float uSpotlightRadius;
uniform float uSpotlightSoftness;
uniform float uSpotlightOpacity;
uniform float uMirror;
uniform float uDistort;
uniform float uShineFlip;

uniform vec3 uColor0;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
uniform vec3 uColor4;
uniform vec3 uColor5;
uniform vec3 uColor6;
uniform vec3 uColor7;
uniform int  uColorCount;

varying vec2 vUv;

float rand(vec2 co){
  return fract(sin(dot(co, vec2(12.9898,78.233))) * 43758.5453);
}

vec2 rotate2D(vec2 p, float a){
  float c = cos(a);
  float s = sin(a);
  return mat2(c,-s,s,c)*p;
}

vec3 getGradientColor(float t){
  float tt = clamp(t,0.0,1.0);
  float scaled = tt * float(uColorCount - 1);
  float f = fract(scaled);
  int seg = int(floor(scaled));

  if (seg == 0) return mix(uColor0,uColor1,f);
  if (seg == 1) return mix(uColor1,uColor2,f);
  if (seg == 2) return mix(uColor2,uColor3,f);
  if (seg == 3) return mix(uColor3,uColor4,f);
  if (seg == 4) return mix(uColor4,uColor5,f);
  if (seg == 5) return mix(uColor5,uColor6,f);
  if (seg == 6) return mix(uColor6,uColor7,f);
  return uColor7;
}

void main() {
    vec2 uv0 = vUv;
    float aspect = iResolution.x / iResolution.y;

    vec2 p = uv0 * 2.0 - 1.0;
    p.x *= aspect;
    p = rotate2D(p, uAngle);
    p.x /= aspect;
    vec2 uv = p * 0.5 + 0.5;

    if (uDistort > 0.0) {
        uv += vec2(
            sin(uv.y * 6.0) * 0.01 * uDistort,
            cos(uv.x * 6.0) * 0.01 * uDistort
        );
    }

    float t = uv.x;
    if (uMirror > 0.5) t = 1.0 - abs(1.0 - 2.0 * fract(t));
    vec3 base = getGradientColor(t);

    vec2 m = iMouse / iResolution.xy;
    float d = length(uv0 - m);
    float spot = (1.0 - pow(d / max(uSpotlightRadius,1e-4), uSpotlightSoftness)) * uSpotlightOpacity;

    float stripe = fract(uv.x * max(uBlindCount,1.0));
    if (uShineFlip > 0.5) stripe = 1.0 - stripe;

    vec3 col = base + vec3(spot) - vec3(stripe);
    col += (rand(gl_FragCoord.xy + iTime) - 0.5) * uNoise;

    gl_FragColor = vec4(col,1.0);
}
`;

        const { arr, count } = prepStops(gradientColors);

        const uniforms = {
            iResolution: { value: [1, 1, 1] },
            iMouse: { value: [0, 0] },
            iTime: { value: 0 },

            uAngle: { value: angle * Math.PI / 180 },
            uNoise: { value: noise },
            uBlindCount: { value: blindCount },
            uSpotlightRadius: { value: spotlightRadius },
            uSpotlightSoftness: { value: spotlightSoftness },
            uSpotlightOpacity: { value: spotlightOpacity },
            uMirror: { value: mirrorGradient ? 1 : 0 },
            uDistort: { value: distortAmount },
            uShineFlip: { value: shineDirection === 'right' ? 1 : 0 },

            uColor0: { value: arr[0] },
            uColor1: { value: arr[1] },
            uColor2: { value: arr[2] },
            uColor3: { value: arr[3] },
            uColor4: { value: arr[4] },
            uColor5: { value: arr[5] },
            uColor6: { value: arr[6] },
            uColor7: { value: arr[7] },
            uColorCount: { value: count }
        };

        const program = new Program(gl, { vertex, fragment, uniforms });
        programRef.current = program;

        geometryRef.current = new Triangle(gl);
        meshRef.current = new Mesh(gl, { geometry: geometryRef.current, program });

        const clampToCanvas = (x, y, w, h) => {
            return [
                Math.min(Math.max(x, 0), w),
                Math.min(Math.max(y, 0), h)
            ];
        };



        /* ---------- Resize ---------- */
        const resize = () => {
            const rect = container.getBoundingClientRect();
            const dpr = renderer.dpr || window.devicePixelRatio || 1;

            const cssWidth = rect.width;
            const cssHeight = rect.height;

            const pixelWidth = Math.round(cssWidth * dpr);
            const pixelHeight = Math.round(cssHeight * dpr);

            // 🔥 SET DRAWING BUFFER (GPU SIZE)
            renderer.gl.canvas.width = pixelWidth;
            renderer.gl.canvas.height = pixelHeight;

            // 🔥 FORCE CSS SIZE (LAYOUT SIZE)
            renderer.gl.canvas.style.width = `${cssWidth}px`;
            renderer.gl.canvas.style.height = `${cssHeight}px`;

            // 🔥 UPDATE VIEWPORT
            renderer.gl.viewport(0, 0, pixelWidth, pixelHeight);

            // 🔥 UPDATE SHADER RESOLUTION
            uniforms.iResolution.value = [pixelWidth, pixelHeight, 1];

            // 🔥 BLINDS COUNT SAFETY (supports %, vw, cqw)
            const resolvedMinWidth = resolveDynamicWidth(blindMinWidth, cssWidth);

            if (resolvedMinWidth > 0) {
                const maxByMin = Math.max(1, Math.floor(cssWidth / resolvedMinWidth));
                uniforms.uBlindCount.value = Math.min(blindCount, maxByMin);
            } else {
                uniforms.uBlindCount.value = blindCount;
            }

            // 🔥 INIT CENTER ON FIRST RESIZE
            if (firstResizeRef.current) {
                firstResizeRef.current = false;
                const cx = pixelWidth / 2;
                const cy = pixelHeight / 2;

                uniforms.iMouse.value = [cx, cy];
                mouseTargetRef.current = [cx, cy];
                autoCenterRef.current = [cx, cy];
            }
        };

        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(container);

        /* ---------- Pointer ---------- */
        const onMove = e => {
            const rect = container.getBoundingClientRect();
            const scale = renderer.dpr;

            const rawX = (e.clientX - rect.left) * scale;
            const rawY = (rect.height - (e.clientY - rect.top)) * scale;

            const [cx, cy] = clampToCanvas(
                rawX,
                rawY,
                uniforms.iResolution.value[0],
                uniforms.iResolution.value[1]
            );

            mouseTargetRef.current = [cx, cy];
            isPointerActiveRef.current = true;
        };


        const onLeave = () => {
            isPointerActiveRef.current = false;
            autoCenterRef.current = [...uniforms.iMouse.value];
        };

        const eventTarget = interactionLayer || container;

        eventTarget.addEventListener('pointermove', onMove);
        eventTarget.addEventListener('pointerleave', onLeave);

        // ---------- Loop ----------
        const loop = t => {
            rafRef.current = requestAnimationFrame(loop);
            uniforms.iTime.value = t * 0.001;

            const w = uniforms.iResolution.value[0];
            const h = uniforms.iResolution.value[1];

            if (
                autoAnimate &&
                (!pauseOnHover || !isPointerActiveRef.current)
            ) {
                const v = autoVelocityRef.current;

                const dt = lastTimeRef.current
                    ? (t - lastTimeRef.current) * 0.001
                    : 0.016;

                lastTimeRef.current = t;

                autoCenterRef.current[0] += v[0] * autoSpeed * dt * 600;
                autoCenterRef.current[1] += v[1] * autoSpeed * dt * 600;

                const minX = 0;
                const maxX = w;
                const minY = 0;
                const maxY = h;

                if (autoCenterRef.current[0] < minX) {
                    autoCenterRef.current[0] = minX;
                    v[0] *= -1;
                } else if (autoCenterRef.current[0] > maxX) {
                    autoCenterRef.current[0] = maxX;
                    v[0] *= -1;
                }

                if (autoCenterRef.current[1] < minY) {
                    autoCenterRef.current[1] = minY;
                    v[1] *= -1;
                } else if (autoCenterRef.current[1] > maxY) {
                    autoCenterRef.current[1] = maxY;
                    v[1] *= -1;
                }

                autoCenterRef.current = clampToCanvas(
                    autoCenterRef.current[0],
                    autoCenterRef.current[1],
                    w,
                    h
                );

                // ✅ ONLY AUTO WRITES TARGET HERE
                mouseTargetRef.current = [...autoCenterRef.current];
            }

            const cur = uniforms.iMouse.value;
            const tgt = mouseTargetRef.current;
            const f = 1 - Math.exp(-0.016 / Math.max(mouseDampening, 0.0001));

            cur[0] += (tgt[0] - cur[0]) * f;
            cur[1] += (tgt[1] - cur[1]) * f;

            if (!paused) renderer.render({ scene: meshRef.current });
        };


        rafRef.current = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(rafRef.current);
            eventTarget.removeEventListener('pointermove', onMove);
            eventTarget.removeEventListener('pointerleave', onLeave);
            ro.disconnect();
            container.removeChild(canvas);
            program.remove();
            geometryRef.current.remove();
            // meshRef.current.remove();
            meshRef.current = null;
            // renderer.destroy();
            // ✅ OGL Renderer doesn't always have destroy() depending on version/build
            if (typeof renderer.destroy === "function") {
                renderer.destroy();
            } else {
                // best-effort WebGL cleanup (prevents mobile/context leaks)
                const lose = renderer.gl.getExtension("WEBGL_lose_context");
                if (lose) lose.loseContext();
            }
        };
    }, [
        dpr,
        paused,
        gradientColors,
        angle,
        noise,
        blindCount,
        blindMinWidth,
        mouseDampening,
        mirrorGradient,
        spotlightRadius,
        spotlightSoftness,
        spotlightOpacity,
        distortAmount,
        shineDirection,
        autoAnimate,
        autoSpeed,
        pauseOnHover
    ]);

    return (
        <div
            ref={containerRef}
            className={`gradient-blinds-container ${className ?? ''}`}
            style={mixBlendMode ? { mixBlendMode } : undefined}
        />
    );
};

export default GradientBlinds;